/**
 * Onboarding checklist widget (E2)
 * Floating bottom-right card showing setup steps for new users.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Circle, X, ChevronUp, ChevronDown, Sparkles, Wand2 } from 'lucide-react';
import { API_URL } from '../utils/api';

export default function OnboardingChecklist() {
  const { i18n } = useTranslation();
  const isHu = i18n.language === 'hu';
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(() => typeof window === 'undefined' || window.innerWidth >= 768);
  const [localDismissed, setLocalDismissed] = useState(() => {
    // Tisztelje az onboarding tour befejezését / kihagyását: ha bármelyik flag set,
    // a checklist widget se jelenjen meg (összhangban a tour viselkedésével).
    try {
      if (localStorage.getItem('onboardingCompleted') === 'true') return true;
      if (localStorage.getItem('hasSeenOnboarding') === 'true') return true;
      if (localStorage.getItem('onboarding_dismissed') === '1') return true;
      // Per-user kulcs (OnboardingTour formátum: onboarding_completed_<userId>)
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i) || '';
        if (k.startsWith('onboarding_completed_') && localStorage.getItem(k)) return true;
      }
    } catch (_) { /* ignore */ }
    return false;
  });

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/onboarding/state`, { withCredentials: true });
      setState(r.data);
    } catch {}
  };
  useEffect(() => {
    if (localDismissed) return; // Ne pollozzon, ha a tour már befejeződött
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 120000);
    return () => clearInterval(t);
  }, [localDismissed]);

  // Reagáljon a runtime localStorage változásokra (pl. ha a tour épp most fejeződött be)
  useEffect(() => {
    const check = () => {
      try {
        if (localStorage.getItem('onboardingCompleted') === 'true'
            || localStorage.getItem('hasSeenOnboarding') === 'true'
            || localStorage.getItem('onboarding_dismissed') === '1') {
          setLocalDismissed(true);
        }
      } catch (_) { /* ignore */ }
    };
    window.addEventListener('storage', check);
    window.addEventListener('onboarding:completed', check);
    return () => {
      window.removeEventListener('storage', check);
      window.removeEventListener('onboarding:completed', check);
    };
  }, []);

  const dismiss = async () => {
    try {
      localStorage.setItem('onboarding_dismissed', '1');
    } catch (_) { /* ignore */ }
    setLocalDismissed(true);
    await axios.post(`${API_URL}/api/onboarding/dismiss`, {}, { withCredentials: true });
    setState({ dismissed: true, steps: [], completed_count: 0, total: 0 });
  };

  if (localDismissed) return null;
  if (!state || state.dismissed) return null;
  if (state.completed_count >= state.total) return null; // hide when 100% done

  const pct = state.total > 0 ? Math.round((state.completed_count / state.total) * 100) : 0;

  return (
    <div className="fixed bottom-4 right-4 z-[60] w-80 max-w-[calc(100vw-2rem)]" data-testid="onboarding-widget">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-4 py-3 flex items-center gap-2 text-white">
          <Sparkles size={16} />
          <p className="flex-1 text-sm font-semibold">
            {isHu ? 'Beüzemelés' : 'Začíname'}
          </p>
          <span className="text-xs font-bold bg-white/20 px-2 py-0.5 rounded-full">
            {state.completed_count}/{state.total}
          </span>
          <button onClick={() => setOpen(!open)} className="hover:bg-white/10 p-0.5 rounded">
            {open ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
          <button onClick={dismiss} data-testid="onboarding-dismiss" className="hover:bg-white/10 p-0.5 rounded">
            <X size={14} />
          </button>
        </div>
        {/* Progress bar */}
        <div className="h-1.5 bg-slate-100">
          <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 transition-all" style={{ width: `${pct}%` }} />
        </div>
        {open && (
          <div className="p-2 space-y-0.5">
            {state.steps.some((s) => s.key === 'company_info' && !s.done) && (
              <Link to="/setup-wizard" data-testid="onboarding-setup-wizard" className="flex items-center gap-2 px-2 py-2 mb-1 rounded-lg text-sm font-semibold text-white" style={{ background: '#B85A26' }}>
                <Wand2 size={15} /> {isHu ? 'Cég-beállító varázsló indítása' : 'Spustiť sprievodcu nastavením firmy'}
              </Link>
            )}
            {state.steps.map((s) => (
              <Link key={s.key} to={s.url}
                data-testid={`onboarding-step-${s.key}`}
                className={`flex items-center gap-2 px-2 py-2 rounded-lg text-sm hover:bg-slate-50 ${
                  s.done ? 'text-slate-400' : 'text-slate-700'
                }`}>
                {s.done
                  ? <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />
                  : <Circle size={16} className="text-slate-300 flex-shrink-0" />}
                <span className={s.done ? 'line-through' : ''}>
                  {isHu ? s.title_hu : s.title_sk}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
